<template>
  <div class="tw:pb-6">
    <div
      v-for="paymentMethod in renderList"
      :key="paymentMethod.key"
      class="tw:flex tw:flex-col"
      :class="renderList.length > 1 ? 'tw:py-3' : ''"
    >
      <div class="tw:flex tw:items-center tw:w-full">
        <input
          type="radio"
          name="paymentMethod"
          :value="paymentMethod?.pageType"
          :id="paymentMethod?.pageId"
          :checked="selectedPaymentMethod === paymentMethod?.pageType"
          @change="onPaymentMethodTypeChange(paymentMethod)"
          class="tw:cursor-pointer"
        />
        <label
          :for="paymentMethod?.pageId"
          class="tw:cursor-pointer tw:m-0 tw:ml-2 tw:grow tw:flex tw:items-center tw:flex-wrap"
        >
          <span class="tw:grow" v-if="paymentMethod?.pageType === 'CREDIT_CARD'">{{ $t('form.form.paymentMethods.new.CREDIT_CARD') }}</span>
          <span v-if="paymentMethod?.pageType === 'SEPA'" class="tw:grow">{{ $t('form.paymentMethods.SEPA') }}</span>
          <span v-if="paymentMethod?.pageType === 'PAYPAL'" class="tw:grow">PayPal</span>
          <div v-if="paymentMethod?.pageType === 'CREDIT_CARD'" class="tw:w-[140px]">
            <img
              :src="visaLogo"
              alt="Visa logo"
              height="24"
              width="38"
              class="shadow-[0_0_1px_0_rgba(41,52,58,0.75)] tw:mr-2 tw:inline"
            />
            <img
              :src="mastercardLogo"
              alt="Mastercard logo"
              height="24"
              width="38"
              class="shadow-[0_0_1px_0_rgba(41,52,58,0.75)] tw:mr-2 tw:inline"
            />
            <img
              :src="americanexpressLogo"
              alt="American Express logo"
              height="24"
              width="38"
              class="shadow-[0_0_1px_0_rgba(41,52,58,0.75)] tw:mr-2 tw:inline"
            />
          </div>
          <div v-if="paymentMethod?.pageType === 'PAYPAL'" class="tw:w-[50px]">
            <img
                :src="paypalLogo"
                alt="Paypal logo"
                height="24"
                width="38"
                class="shadow-[0_0_1px_0_rgba(41,52,58,0.75)] tw:mr-2 tw:inline"
            />
          </div>
        </label>
      </div>
      <div>
        <div
          v-if="fetchingPaymentMethods && selectedPaymentItem?.pageType === paymentMethod.pageType"
          class="overlay-white-75 tw:h-10 tw:mb-5 tw:w-full"
        >
          <LaunchpadLoader class="tw:h-full tw:flex tw:justify-center tw:items-center" />
        </div>

        <div
          v-if="paymentMethod?.pageType === 'PAYPAL' && selectedPaymentItem?.pageType === 'PAYPAL'"
          class="tw:px-4 tw:pt-4"
          id="paypal-button-container"
        />
        <div
          v-else-if="selectedPaymentItem?.pageType === paymentMethod?.pageType"
          class="tw:px-4 tw:pt-4"
          id="zuora_payment"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { sepaCountries } from '@/constants/sepaCountries.ts'
import { useErrorMetrics, useCreatePayPalPaymentSession } from '@/components/payment/queries/index.js'
import { useScrollTo } from '@/composables/useScrollTo'
import { useSessionValues } from '@/composables/useSessionValues'

import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import zuoraMetricConstants from '@/constants/zuoraMetricConstants'
import usePaymentNotifications from '@/components/notifications/usePaymentNotifications'

const { t } = useI18n()
const route = useRoute()
const { scrollToTop } = useScrollTo()
const { displayPaymentNotification, clearAllNotifications } = usePaymentNotifications()
const props = defineProps({
  accommodationId: Number,
  currency: {
    type: String,
    default: 'EUR',
  },
  selectedPaymentMethod: {
    type: String,
    default: null,
  },
  availablePaymentMethods: {
    type: Array,
    default: () => [],
  },
  paypalConfig: {
    type: Object,
    default: null,
  },
  fetchingPaymentMethods: {
    type: Boolean,
    default: false,
  },
  productName: String,
})

const emit = defineEmits(['onChange', 'onZuoraCallbackSuccess', 'onZuoraCallbackError', 'onZuoraClientValidationError'])

const visaLogo = new URL('/src/assets/img/settings-payment/visa.png', import.meta.url)
const paypalLogo = new URL('/src/assets/img/settings-payment/paypal.png', import.meta.url)
const mastercardLogo = new URL('/src/assets/img/settings-payment/mastercard.png', import.meta.url)
const americanexpressLogo = new URL('/src/assets/img/settings-payment/americanexpress.png', import.meta.url)

const { sendErrorMetrics } = useErrorMetrics()
const { createPayPalPaymentSession } = useCreatePayPalPaymentSession()
const { language } = useSessionValues()

const countryWhiteList = computed(() => sepaCountries?.map(country => country.country_code))

// Hardcoded currency for the pilot. Users are going to be based in EU countries.
const currency = props.currency ?? 'EUR'

const selectedPaymentItem = ref(null)

// PayPal isn't part of the Zuora Hosted Payment Pages list; it's driven by a
// separate `paypal` config object. When present, surface it as an extra option.
// CAUTION: pageId becomes the radio input's DOM id. It must NOT be "paypal" —
// any element id becomes a window global (window.paypal = <input>), which shadows
// the PayPal SDK namespace and crashes its bootstrap with
// "Cannot read properties of undefined (reading 'startsWith')".
const PAYPAL_ITEM = { pageType: 'PAYPAL', pageId: 'paypal-payment-method', key: 'paypal' }
const shouldShowPaypal = computed(() => {
  const queryValue = route.query.showPaypal
  const normalizedValue = Array.isArray(queryValue) ? queryValue[0] : queryValue

  return normalizedValue === true || normalizedValue === 'true'
})

const renderList = computed(() => {
  const list = [...(props.availablePaymentMethods ?? [])]
  if (props.paypalConfig && shouldShowPaypal.value) list.push(PAYPAL_ITEM)
  return list
})

// Re-render whichever payment surface is currently selected.
const renderSelectedPaymentSurface = () => {
  if (selectedPaymentItem.value?.pageType === 'PAYPAL') {
    renderPayPalButton()
  } else {
    renderZuoraHpp()
  }
}

watch(
  () => props.availablePaymentMethods,
  (availablePaymentMethods, prevAvailablePaymentMethods) => {
    if (prevAvailablePaymentMethods !== availablePaymentMethods) {
      // PayPal is synthetic, so it won't be found in the fetched list — keep it selected as-is.
      if (selectedPaymentItem.value?.pageType !== 'PAYPAL') {
        selectedPaymentItem.value = props.availablePaymentMethods.find(
          item => item?.pageType === selectedPaymentItem.value?.pageType
        )
      }

      nextTick(() => {
        renderSelectedPaymentSurface()
      })
    }
  }
)

watch(language, () => {
  renderSelectedPaymentSurface()
})

const onPaymentMethodTypeChange = paymentMethod => {
  selectedPaymentItem.value = paymentMethod
  emit('onChange', paymentMethod?.pageType)
  nextTick(() => {
    renderSelectedPaymentSurface()
  })
}
// map Zuora error codes to translation keys
const parseErrorFromCode = code => {
  switch (code) {
    case '001':
      return 'required_field_empty'
    case '002':
    case '003':
      return 'payment_invalid_card_number'
    case '004':
      return 'payment_cvc_declined'
    default:
      return 'payment_declined_generic'
  }
}

// map Zuora error messages to translation keys
const parseErrorFromMessage = (message = 'unknown') => {
  if (message.startsWith('[BusinessValidationError]')) {
    return 'payment_expired_card'
  } else {
    const gatewayTransactionErrorMap = {
      'declined.6 - Expired Card': 'payment_expired_card',
      'declined.103 - CVC is not the right length': 'payment_invalid_cvc_length',
      'declined.24 - CVC Declined': 'payment_invalid_cvc_length',
      'declined.2 - Refused': 'payment_restricted_card',
      'declined.23 - Transaction Not Permitted': 'payment_restricted_card',
      'declined.25 - Restricted Card': 'payment_restricted_card',
      'declined.8 - Invalid Card Number': 'payment_invalid_card_number',
      'declined.112 - This bank country is not supported': 'payment_unsupported_bank_country',
      'declined.701': 'payment_inconsistent_iban',
      'declined.111 - Invalid BankCountryCode specified': 'payment_inconsistent_iban',
      'declined.161 - Invalid iban': 'payment_inconsistent_iban',
      'declined.203 - Invalid Bank account holder name': 'invalid_account_holder',
    }

    const match = message.match(/declined\.(\d+)/i)

    if (match) {
      const errorPart = match[0].trim()
      const matchingKey = Object.keys(gatewayTransactionErrorMap).find(key => {
        const keyCode = key.split('-')[0].trim()
        return keyCode === errorPart
      })

      return matchingKey ? gatewayTransactionErrorMap[matchingKey] : 'payment_declined_generic'
    } else {
      return 'payment_declined_generic'
    }
  }
}

const handleErrorMetric = (originalMessage, mappedMessage, validationCode) => {
  const errorCodes = /000|400|401|403|404|422|500/
  const isClientValidationCode = validationCode !== 'unknown' || originalMessage.includes('BusinessValidationError')
  const isGatewayTransactionError = originalMessage.includes('GatewayTransactionError')
  const isConfigurationError = isGatewayTransactionError && errorCodes.test(originalMessage) && !isClientValidationCode

  const metricPayload = {
    payload: {
      original_error: originalMessage,
      mapped_error: mappedMessage,
    },
    code: zuoraMetricConstants.metricErrorCodes.paymentError,
    message: `Error while processing payment - ${t(mappedMessage)} - ${originalMessage} `,
  }

  if (isConfigurationError || (!isGatewayTransactionError && !isClientValidationCode)) {
    metricPayload.code = zuoraMetricConstants.metricErrorCodes.configurationError
    metricPayload.message = `Zuora HPP configuration error - ${t(mappedMessage)}`
  } else if (isClientValidationCode) {
    metricPayload.code = zuoraMetricConstants.metricErrorCodes.clientSideValidationError
    metricPayload.message = `Zuora HPP client side validation error - ${t(mappedMessage)}`
  }
  sendErrorMetrics({
    itemId: props.accommodationId,
    productName: props.productName,
    code: metricPayload.code,
    message: `Error: ${metricPayload.message} | HPP error: ${originalMessage} | Validation code: ${validationCode}`,
  })
}

const renderZuoraHpp = () => {
  const params = {
    style: 'inline',
    submitEnabled: true,
    locale: language.value ? language.value.replace('-', '_') : 'en_US',
    currency: currency,
    token: selectedPaymentItem.value?.token,
    signature: selectedPaymentItem.value?.signature,
    key: selectedPaymentItem.value?.key,
    url: selectedPaymentItem.value?.zuoraHppAppUrl,
    tenantId: selectedPaymentItem.value?.tenantId,
    id: selectedPaymentItem.value?.pageId,
  }

  if (selectedPaymentItem.value?.pageType === 'SEPA') params.countryWhiteList = countryWhiteList.value
  Z.renderWithErrorHandler(params, {}, onZuoraCallback, onZuoraClientErrorCallback)
}

let isRenderingPayPalButton = false

const renderPayPalButton = async () => {
  if (!props.paypalConfig || typeof Zuora === 'undefined') {
    sendErrorMetrics({
      itemId: props.accommodationId,
      productName: props.productName,
      code: zuoraMetricConstants.metricErrorCodes.configurationError,
      message: 'PayPal config or Zuora Payments SDK is not available',
    })
    return
  }

  if (isRenderingPayPalButton) return
  const container = document.querySelector('#paypal-button-container')
  if (!container || container.childElementCount > 0) return

  isRenderingPayPalButton = true
  try {
    const zuora = Zuora(props.paypalConfig.zuoraPublishableKey)
    const paypalButton = await zuora.createPayPalButton({
      // countryCode: 'DE', i am not sure if it's needed or not
      locale: language?.value?.replace('-', '_') || 'en_US',
      currency,
      // Fixed amount for now, per current requirements.
      amount: '1000',
      vault: true,
      provider: {
        name: 'PaypalCP',
        paymentGatewayId: props.paypalConfig.paymentGatewayId,
        clientId: props.paypalConfig.paypalClientId,
      },
      // Backend creates the payment session and returns its token.
      createPaymentSession: () =>
        createPayPalPaymentSession({ amount: 1000, currency }).then(res => res?.data?.token),
      onComplete: result => {
        if (result?.success) {
          emit('onZuoraCallbackSuccess', result.paymentMethodId)
        } else {
          sendErrorMetrics({
            itemId: props.accommodationId,
            productName: props.productName,
            code: result?.error?.code,
            message: result?.error?.message,
          })
          emit('onZuoraCallbackError', false)
        }
      },
    })

    // The container may have been unmounted or filled while the SDK was loading.
    const target = document.querySelector('#paypal-button-container')
    if (target && target.childElementCount === 0) {
      paypalButton.mount('#paypal-button-container')
    }
  } catch (error) {
    sendErrorMetrics({
      itemId: props.accommodationId,
      productName: props.productName,
      code: zuoraMetricConstants.metricErrorCodes.configurationError,
      message: `Failed to create the PayPal button: ${error?.message || 'Unknown error'}`,
    })
  } finally {
    isRenderingPayPalButton = false
  }
}

const onZuoraClientErrorCallback = (key, code, message) => {
  let errorMessageKey

  if (['001', '002', '003', '004'].includes(code)) {
    errorMessageKey = parseErrorFromCode(code)
  } else {
    errorMessageKey = parseErrorFromMessage(message)
  }

  const translationKey = `checkout_errors.payment.ar-studio-subscription-manager_${errorMessageKey}`
  const errorMessage = t(translationKey)

  handleErrorMetric(message, translationKey, code)

  if (key !== 'error') {
    Z.sendErrorMessageToHpm(key, errorMessage)
  } else if (!message.includes('Attempt_Exceed_Limitation')) {
    clearAllNotifications()
    displayPaymentNotification({
      message: errorMessage,
      header: 'payment_method_declined',
    })
    scrollToTop()
  }
  const isAttemptExceedLimitationError = message.includes('Attempt_Exceed_Limitation')
  emit('onZuoraClientValidationError', errorMessage, isAttemptExceedLimitationError)
}

const onZuoraCallback = response => {
  if (response.success) {
    emit('onZuoraCallbackSuccess', response.refId)
  } else {
    sendErrorMetrics({
      itemId: props.accommodationId,
      productName: props.productName,
      code: response.errorCode,
      message: response.errorMessage,
    })
    if (response.errorCode === 'Attempt_Exceed_Limitation') {
      emit('onZuoraCallbackError', true)
    } else {
      emit('onZuoraCallbackError', false)
    }
  }
}
</script>
<style>
.error-field:empty {
  display: none;
}
</style>
